import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import './PricingPage.css';

const PLANS = {
  free: {
    name: 'Starter',
    tagline: 'Perfect for testing the waters',
    price: '$0',
    period: 'for 50 minutes',
    billing: null,
    setup: null,
    features: [
      'Up to 50 minutes of call time',
      'Basic voice agent setup',
      'Call recording & transcripts',
      'Email support',
    ],
    cta: 'Try It Free',
    ctaStyle: 'ghost',
    trust: 'No credit card required',
    link: null,
  },
  standard: {
    name: 'Standard',
    tagline: 'Everything you need to scale',
    monthly: { price: '$549', billing: 'Billed monthly' },
    annual: { price: '$499', billing: 'Billed $5,988/year' },
    setup: '$399 one-time setup',
    features: [
      'Unlimited AI voice calls',
      'Custom voice & personality',
      'CRM integration',
      'Real-time analytics dashboard',
      'Appointment scheduling & lead qualification',
      'Dedicated account manager',
    ],
    cta: 'Get Started',
    ctaStyle: 'primary',
    trust: 'Cancel anytime · No hidden fees',
    monthlyLink: 'https://buy.stripe.com/00w7sMf0ogW1d2S4o56Ri07',
    annualLink: 'https://buy.stripe.com/5kQ3cwf0o0X32oe6wd6Ri06',
  },
  enterprise: {
    name: 'Enterprise',
    tagline: 'For teams with advanced needs',
    price: 'Custom',
    period: null,
    billing: 'Tailored to your business',
    setup: null,
    features: [
      'Everything in Standard',
      'Unlimited concurrent agents',
      'Custom AI model training',
      'White-label & API access',
      'SLA guarantee & dedicated infra',
      'Quarterly strategy reviews',
    ],
    cta: 'Contact Sales',
    ctaStyle: 'ghost',
    trust: "Let's build your perfect plan",
    link: null,
  },
};

const ALL_FEATURES = [
  { name: 'AI voice calls', free: '50 min', standard: 'Unlimited', enterprise: 'Unlimited' },
  { name: 'Custom voice & personality', free: false, standard: true, enterprise: true },
  { name: 'Call recording & transcripts', free: true, standard: true, enterprise: true },
  { name: 'CRM integration', free: false, standard: true, enterprise: true },
  { name: 'Real-time analytics', free: false, standard: true, enterprise: true },
  { name: 'Appointment scheduling', free: false, standard: true, enterprise: true },
  { name: 'Lead qualification', free: false, standard: true, enterprise: true },
  { name: 'Call routing & transfers', free: false, standard: true, enterprise: true },
  { name: 'Multi-language support', free: false, standard: true, enterprise: true },
  { name: 'Custom greetings & scripts', free: false, standard: true, enterprise: true },
  { name: 'Voicemail detection', free: false, standard: true, enterprise: true },
  { name: 'API access', free: false, standard: 'Basic', enterprise: 'Advanced' },
  { name: 'Dedicated account manager', free: false, standard: true, enterprise: true },
  { name: 'Custom AI training', free: false, standard: false, enterprise: true },
  { name: 'White-label options', free: false, standard: false, enterprise: true },
  { name: 'SLA guarantee', free: false, standard: false, enterprise: true },
  { name: 'Dedicated infrastructure', free: false, standard: false, enterprise: true },
];

const FAQ_ITEMS = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel your subscription at any time — no cancellation fees. Cancellation is subject to your contract terms. You\'ll keep access until the end of your billing period.',
  },
  {
    q: 'What happens after my free trial?',
    a: 'After your 50 minutes are used, you can upgrade to a paid plan or your account stays active with no charges. We\'ll never auto-bill you without consent.',
  },
  {
    q: 'What does the setup fee cover?',
    a: 'The $399 one-time fee covers custom voice agent configuration, CRM integration, script development, and a live onboarding session with our team.',
  },
  {
    q: 'Can I switch between monthly and annual billing?',
    a: 'Absolutely. You can switch from monthly to annual billing at any time to lock in the lower rate. Switching from annual to monthly takes effect at your next renewal.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, Amex) and ACH bank transfers for annual plans. All payments are processed securely through Stripe.',
  },
];

const PricingPage = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const goToContact = () => navigate('/#contact');

  const std = PLANS.standard;
  const stdData = isAnnual ? std.annual : std.monthly;

  return (
    <section className="pp-section">
      <div className="pp-container">
        {/* Hero */}
        <div className="pp-hero">
          <p className="pp-label">Pricing</p>
          <h1 className="pp-title">Simple, Transparent Pricing</h1>
          <p className="pp-subtitle">
            No hidden fees. No surprises. Pick the plan that fits your business.
          </p>
        </div>

        {/* Billing Toggle — segmented control */}
        <div className="pp-toggle-wrapper">
          <div className="pp-toggle-control" role="radiogroup" aria-label="Billing period">
            <button
              className={`pp-toggle-btn ${!isAnnual ? 'active' : ''}`}
              onClick={() => setIsAnnual(false)}
              role="radio"
              aria-checked={!isAnnual}
            >
              Monthly
            </button>
            <button
              className={`pp-toggle-btn ${isAnnual ? 'active' : ''}`}
              onClick={() => setIsAnnual(true)}
              role="radio"
              aria-checked={isAnnual}
            >
              Annual
              <span className="pp-save-badge">Save $600</span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="pp-grid">
          {/* Free */}
          <div className="pp-card">
            <div className="pp-card-inner">
              <div className="pp-card-top">
                <h3 className="pp-plan-name">{PLANS.free.name}</h3>
                <p className="pp-plan-tag">{PLANS.free.tagline}</p>
                <div className="pp-price-row">
                  <span className="pp-price">{PLANS.free.price}</span>
                  <span className="pp-period">{PLANS.free.period}</span>
                </div>
              </div>
              <ul className="pp-features">
                {PLANS.free.features.map((f, i) => (
                  <li key={i}><Check size={16} strokeWidth={2.5} /><span>{f}</span></li>
                ))}
              </ul>
              <div className="pp-card-bottom">
                <button className="pp-btn pp-btn--ghost" onClick={goToContact}>
                  {PLANS.free.cta}
                </button>
                <p className="pp-trust">{PLANS.free.trust}</p>
              </div>
            </div>
          </div>

          {/* Standard */}
          <div className="pp-card pp-card--featured">
            <div className="pp-badge">Most Popular</div>
            <div className="pp-card-inner">
              <div className="pp-card-top">
                <h3 className="pp-plan-name">{std.name}</h3>
                <p className="pp-plan-tag">{std.tagline}</p>
                <div className="pp-price-row">
                  <span className="pp-price">{stdData.price}</span>
                  <span className="pp-period">/month</span>
                </div>
                <p className="pp-billing">{stdData.billing}</p>
                <p className="pp-setup">{std.setup}</p>
              </div>
              <ul className="pp-features">
                {std.features.map((f, i) => (
                  <li key={i}><Check size={16} strokeWidth={2.5} /><span>{f}</span></li>
                ))}
              </ul>
              <div className="pp-card-bottom">
                <a
                  className="pp-btn pp-btn--primary"
                  href={isAnnual ? std.annualLink : std.monthlyLink}
                  rel="noopener noreferrer"
                >
                  {std.cta}
                </a>
                <p className="pp-trust">{std.trust}</p>
              </div>
            </div>
          </div>

          {/* Enterprise */}
          <div className="pp-card">
            <div className="pp-card-inner">
              <div className="pp-card-top">
                <h3 className="pp-plan-name">{PLANS.enterprise.name}</h3>
                <p className="pp-plan-tag">{PLANS.enterprise.tagline}</p>
                <div className="pp-price-row">
                  <span className="pp-price">{PLANS.enterprise.price}</span>
                </div>
                <p className="pp-billing">{PLANS.enterprise.billing}</p>
              </div>
              <ul className="pp-features">
                {PLANS.enterprise.features.map((f, i) => (
                  <li key={i}><Check size={16} strokeWidth={2.5} /><span>{f}</span></li>
                ))}
              </ul>
              <div className="pp-card-bottom">
                <button className="pp-btn pp-btn--ghost" onClick={goToContact}>
                  {PLANS.enterprise.cta}
                </button>
                <p className="pp-trust">{PLANS.enterprise.trust}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compare All Features */}
        <div className="pp-compare-section">
          <button
            className="pp-compare-toggle"
            onClick={() => setShowComparison(!showComparison)}
          >
            {showComparison ? 'Hide' : 'Compare all'} features
            {showComparison ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {showComparison && (
            <div className="pp-compare-table-wrap">
              <table className="pp-compare-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Starter</th>
                    <th className="pp-col-highlight">Standard</th>
                    <th>Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_FEATURES.map((row, i) => (
                    <tr key={i}>
                      <td>{row.name}</td>
                      <td>{renderCell(row.free)}</td>
                      <td className="pp-col-highlight">{renderCell(row.standard)}</td>
                      <td>{renderCell(row.enterprise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Social Proof */}
        <div className="pp-social-proof">
          <p className="pp-social-label">Trusted by leading businesses</p>
          <div className="pp-logos">
            {[
              { name: 'Pizza Hut', logo: 'https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=375,fit=crop/mxB4Gjwo2Xh7OnGZ/pizzahutlogo-AR0LQveDDqI99qPn.jpg' },
              { name: 'Wayback Burgers', logo: 'https://assets.zyrosite.com/mxB4Gjwo2Xh7OnGZ/wayback_burgers_logo-m5KM46PZXDSWGD0a.svg' },
              { name: 'Which Wich', logo: 'https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=375,fit=crop/mxB4Gjwo2Xh7OnGZ/whichwichlogo-mjE7OpzXKrukJxQJ.png' },
              { name: 'Gloria Jeans', logo: 'https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=375,fit=crop/mxB4Gjwo2Xh7OnGZ/gloriajeanslogo-m2W8yz12NecnbPKk.webp' },
              { name: 'Shell', logo: 'https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=375,fit=crop/mxB4Gjwo2Xh7OnGZ/shellgasstationlogo-AMq15L29g5tpOl0G.png' },
              { name: 'The Flying Biscuit', logo: '/flying-biscuit-logo.png' },
            ].map((client) => (
              <img
                key={client.name}
                src={client.logo}
                alt={client.name}
                className="pp-logo-img"
                loading="lazy"
              />
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="pp-faq">
          <h2 className="pp-faq-title">Frequently Asked Questions</h2>
          <div className="pp-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className={`pp-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button
                  className="pp-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{item.q}</span>
                  {openFaq === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openFaq === i && <p className="pp-faq-a">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

function renderCell(value) {
  if (value === true) return <Check size={16} className="pp-check" />;
  if (value === false) return <span className="pp-dash">—</span>;
  return <span className="pp-cell-text">{value}</span>;
}

export default PricingPage;
